import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Download, Check, Clock, Target, Calendar, AlertTriangle } from "lucide-react";
import { Brand } from "@/components/Brand";
import { getReport, reportPdfUrl } from "@/lib/api";
import { toast } from "sonner";

const CTA_URL = "https://techport.nl/contact/";

export default function Report() {
  const { sessionId } = useParams();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const data = await getReport(sessionId);
        setReport(data.report);
      } catch {
        toast.error("Rapport niet gevonden.");
      } finally {
        setLoading(false);
      }
    })();
  }, [sessionId]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="font-condensed uppercase tracking-[0.25em] text-sm text-[#6B6B85] flex items-center gap-3">
          <span className="w-2 h-2 bg-[#3FCBA6] animate-pulse" />
          Rapport laden...
        </div>
      </div>
    );
  }

  if (!report) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-white gap-4 px-6">
        <h2 className="font-display text-5xl text-[#1B1B3A]">Rapport niet gevonden</h2>
        <Link to="/" className="font-condensed uppercase text-sm tracking-[0.2em] text-[#3FCBA6] hover:underline font-semibold">
          ← Terug naar start
        </Link>
      </div>
    );
  }

  const name = report.naam || "Jouw";
  const rol = report.rol || "";
  const org = report.organisatie || "";

  return (
    <div className="min-h-screen bg-white text-[#1B1B3A]">
      {/* Top bar */}
      <header className="border-b-2 border-[#1B1B3A] bg-white sticky top-0 z-20">
        <div className="max-w-[1500px] mx-auto px-6 lg:px-12 h-20 flex items-center justify-between">
          <Brand />
          <a
            href={reportPdfUrl(sessionId)}
            target="_blank"
            rel="noopener noreferrer"
            className="tp-btn tp-btn--mint text-sm"
            data-testid="download-pdf-button"
          >
            <Download className="w-4 h-4" strokeWidth={2.5} />
            Download PDF
          </a>
        </div>
      </header>

      {/* Title block — Techport hero style */}
      <section className="ink-bg relative overflow-hidden">
        <div className="absolute top-0 right-0 w-1/3 h-full opacity-20" style={{background: "radial-gradient(circle at 70% 30%, #3FCBA6 0%, transparent 60%)"}} />
        <div className="relative max-w-[1500px] mx-auto px-6 lg:px-12 py-20 lg:py-32">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="font-condensed uppercase tracking-[0.3em] text-[13px] text-[#3FCBA6] font-semibold mb-6">
              · Techport AI Check · Vertrouwelijk · Doc/{sessionId.slice(0, 6).toUpperCase()} ·
            </div>
            <h1 className="font-display text-white text-[56px] sm:text-7xl lg:text-[140px]">
              {name},<br />
              <span className="italic-em" style={{ fontStyle: "italic" }}>jullie AI</span><br />
              Business Case.
            </h1>
            {(rol || org) && (
              <p className="mt-8 font-condensed uppercase text-sm tracking-[0.2em] text-white/60 font-semibold">
                {[rol, org].filter(Boolean).join(" · ")}
              </p>
            )}
          </motion.div>
        </div>
      </section>

      {/* Profile & key observation */}
      <section className="bg-white border-b-2 border-[#1B1B3A]">
        <div className="max-w-[1500px] mx-auto px-6 lg:px-12 py-20 grid grid-cols-12 gap-8">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="col-span-12 lg:col-span-7"
            data-testid="profile-card"
          >
            <div className="font-condensed uppercase tracking-[0.25em] text-sm text-[#6B6B85] font-semibold mb-4">
              · 01 · jouw profiel ·
            </div>
            <p className="font-body text-lg lg:text-xl text-[#1B1B3A] leading-[1.5] font-medium">
              {report.profiel?.samenvatting}
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="col-span-12 lg:col-span-5 mint-bg p-10 flex flex-col justify-between min-h-[260px] relative"
            data-testid="kern-card"
          >
            <div>
              <div className="flex items-center gap-3 mb-6">
                <Target className="w-5 h-5 text-[#1B1B3A]" strokeWidth={2.5} />
                <span className="font-condensed uppercase text-xs tracking-[0.25em] font-semibold text-[#1B1B3A]/80">
                  · Kernobservatie ·
                </span>
              </div>
              <p className="font-body text-base lg:text-lg text-[#1B1B3A] leading-[1.5] font-medium">
                {report.profiel?.kernobservatie}
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Use cases */}
      <section className="bg-[#F8F8FA] border-b-2 border-[#1B1B3A]">
        <div className="max-w-[1500px] mx-auto px-6 lg:px-12 py-20">
          <div className="grid grid-cols-12 gap-8 mb-14">
            <div className="col-span-12 lg:col-span-6">
              <div className="font-condensed uppercase tracking-[0.25em] text-sm text-[#6B6B85] font-semibold mb-4">
                · 02 · drie AI business cases ·
              </div>
              <h2 className="font-display text-3xl lg:text-5xl text-[#1B1B3A]">
                Waar de winst<br />zit.
              </h2>
            </div>
            <div className="col-span-12 lg:col-span-5 lg:col-start-8 flex items-end">
              <p className="text-[#1B1B3A]/75 text-lg leading-relaxed">
                Elke business case is gekoppeld aan iets wat jij letterlijk zei tijdens de intake.
                Geen generieke beloftes — concrete besparing per jaar, gericht op jullie context.
              </p>
            </div>
          </div>

          <div className="space-y-6">
            {(report.use_cases || []).map((uc, i) => {
              const type = (uc.type || "").toLowerCase();
              const isQuickWin = type === "quick_win" || (i === 0 && !type);
              const badge = isQuickWin
                ? { text: "· Quick win · live binnen 30 dagen ·", color: "bg-[#3FCBA6] text-[#1B1B3A]" }
                : { text: "· Strategisch · 3—9 maanden ·", color: "bg-[#1B1B3A] text-[#3FCBA6]" };
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.08 }}
                  className="bg-white border-2 border-[#1B1B3A] grid grid-cols-12 gap-8 p-8 lg:p-12 hover:bg-[#3FCBA6] transition-colors group"
                  data-testid={`use-case-${i + 1}`}
                >
                  {/* LEFT: Big number + badge */}
                  <div className="col-span-12 lg:col-span-3 flex flex-col">
                    <div className="font-display text-[160px] lg:text-[200px] text-[#3FCBA6] group-hover:text-[#1B1B3A] transition-colors leading-none">
                      {String(i + 1).padStart(2, "0")}
                    </div>
                    <span
                      className={`mt-2 inline-block self-start px-3 py-1.5 font-condensed uppercase text-[10px] tracking-[0.2em] font-semibold ${badge.color}`}
                      data-testid={`use-case-${i + 1}-badge`}
                    >
                      {badge.text}
                    </span>
                  </div>

                  {/* RIGHT: Content */}
                  <div className="col-span-12 lg:col-span-9 flex flex-col">
                    <h3 className="font-display text-3xl lg:text-5xl text-[#1B1B3A] mb-6 leading-tight">
                      {uc.naam}
                    </h3>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                      <div>
                        <div className="font-condensed uppercase text-[11px] tracking-[0.2em] font-semibold text-[#6B6B85] group-hover:text-[#1B1B3A]/70 mb-1.5">
                          · Wat het oplost ·
                        </div>
                        <p className="text-[#1B1B3A] leading-relaxed text-base">{uc.wat_het_oplost}</p>
                      </div>
                      <div>
                        <div className="font-condensed uppercase text-[11px] tracking-[0.2em] font-semibold text-[#6B6B85] group-hover:text-[#1B1B3A]/70 mb-1.5">
                          · In de praktijk ·
                        </div>
                        <p className="text-[#1B1B3A]/85 leading-relaxed text-base">{uc.in_de_praktijk}</p>
                      </div>
                    </div>

                    <div className="bg-[#1B1B3A] text-white p-5">
                      <div className="font-condensed uppercase text-[11px] tracking-[0.2em] font-semibold text-[#3FCBA6] mb-1.5">
                        · Morgen al proberen ·
                      </div>
                      <p className="leading-relaxed text-base">{uc.morgen_stap}</p>
                    </div>

                    <div className="mt-6 pt-5 border-t-2 border-[#1B1B3A] flex items-start gap-3">
                      <Clock className="w-5 h-5 text-[#1B1B3A] mt-0.5 flex-shrink-0" strokeWidth={2.5} />
                      <div className="flex-1">
                        <div className="font-condensed uppercase text-[11px] tracking-[0.2em] font-semibold text-[#6B6B85] group-hover:text-[#1B1B3A]/80 mb-1.5">
                          Geschatte impact per jaar
                        </div>
                        <div className="font-body text-base lg:text-lg text-[#1B1B3A] font-semibold leading-relaxed">
                          {uc.tijdwinst_per_week}
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* 30 days timeline */}
      <section className="mint-bg border-b-2 border-[#1B1B3A]">
        <div className="max-w-[1500px] mx-auto px-6 lg:px-12 py-20">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
            <div>
              <div className="font-condensed uppercase tracking-[0.25em] text-sm font-semibold text-[#1B1B3A]/70 mb-4 flex items-center gap-3">
                <Calendar className="w-3.5 h-3.5" strokeWidth={2.5} />
                · 03 · binnen 90 dagen concreet resultaat ·
              </div>
              <h2 className="font-display text-3xl lg:text-5xl text-[#1B1B3A]">
                Van case<br />naar resultaat.
              </h2>
            </div>
            <div className="font-condensed uppercase text-sm tracking-[0.25em] text-[#1B1B3A]/70 font-semibold">
              Maand 01 → Maand 03
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { w: "Maand 01", label: "Assessment", d: report.dertig_dagen?.week_1, range: "Week 01—04" },
              { w: "Maand 02", label: "Pilot", d: report.dertig_dagen?.week_2, range: "Week 05—08" },
              { w: "Maand 03", label: "Schaal", d: report.dertig_dagen?.week_3_4, range: "Week 09—12" },
            ].map((w, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.1 }}
                className="bg-white border-2 border-[#1B1B3A] p-8"
                data-testid={`week-${i + 1}`}
              >
                <div className="flex items-center justify-between mb-6">
                  <div className="font-display text-2xl text-[#1B1B3A]">
                    {w.w}
                  </div>
                  <div className="font-condensed uppercase text-[11px] tracking-[0.2em] text-[#6B6B85] font-semibold">
                    {w.range}
                  </div>
                </div>
                <h3 className="font-display text-4xl text-[#3FCBA6] mb-4">{w.label}</h3>
                <p className="text-[#1B1B3A]/85 leading-relaxed">{w.d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Blockade */}
      {report.blokkade && (report.blokkade.wat || (report.blokkade.tips || []).length > 0) && (
        <section className="bg-white border-b-2 border-[#1B1B3A]">
          <div className="max-w-[1500px] mx-auto px-6 lg:px-12 py-20 grid grid-cols-12 gap-8">
            <div className="col-span-12 lg:col-span-5">
              <div className="flex items-center gap-3 font-condensed uppercase tracking-[0.25em] text-sm font-semibold text-[#6B6B85] mb-4">
                <AlertTriangle className="w-3.5 h-3.5" strokeWidth={2.5} />
                · 04 · risico's &amp; belemmeringen ·
              </div>
              <h3 className="font-display text-2xl lg:text-4xl text-[#1B1B3A] leading-[1.1]">
                {report.blokkade.wat}
              </h3>
            </div>
            <div className="col-span-12 lg:col-span-7 space-y-3">
              {(report.blokkade.tips || []).map((t, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  className="flex items-start gap-5 bg-white border-2 border-[#1B1B3A] p-6 hover:bg-[#3FCBA6] transition-colors group"
                  data-testid={`blokkade-tip-${i + 1}`}
                >
                  <div className="font-display text-3xl text-[#3FCBA6] group-hover:text-[#1B1B3A] transition-colors w-12 flex-shrink-0">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <span className="text-[#1B1B3A] leading-relaxed flex-1 pt-1">{t}</span>
                  <Check className="w-6 h-6 text-[#1B1B3A] flex-shrink-0 mt-1.5" strokeWidth={2.5} />
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="ink-bg relative">
        <div className="max-w-[1500px] mx-auto px-6 lg:px-12 py-20 lg:py-28 grid grid-cols-12 gap-8">
          <div className="col-span-12 lg:col-span-9">
            <div className="font-condensed uppercase tracking-[0.25em] text-sm text-[#3FCBA6] font-semibold mb-6">
              · 05 · volgende stap ·
            </div>
            <h3 className="font-display text-5xl lg:text-7xl text-white mb-8 leading-[1.05]">
              Aan de slag met<br />de <em className="italic-em" style={{ fontStyle: "italic" }}>AI-Werkplaats</em>?
            </h3>
            <p className="text-white/75 mb-10 max-w-2xl text-lg lg:text-xl leading-relaxed">{report.volgende_stap}</p>
            <div className="flex flex-wrap gap-3">
              <a
                href={CTA_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="tp-btn tp-btn--mint"
                data-testid="cta-training-button"
              >
                Bezoek de AI-Werkplaats
              </a>
              <a
                href={reportPdfUrl(sessionId)}
                target="_blank"
                rel="noopener noreferrer"
                className="tp-btn--ghost inline-flex items-center gap-3 px-6 py-3.5 font-condensed uppercase text-sm tracking-[0.15em] font-semibold border-2 border-white/30 text-white hover:bg-white hover:text-[#1B1B3A] transition-colors"
                data-testid="download-pdf-button-bottom"
              >
                <Download className="w-4 h-4" strokeWidth={2.5} />
                Download PDF
              </a>
            </div>
          </div>
          <div className="hidden lg:flex col-span-3 flex-col justify-end">
            <div className="font-display text-[180px] text-white/5 leading-none">
              90
            </div>
            <div className="font-condensed uppercase text-xs tracking-[0.25em] text-[#3FCBA6] font-semibold mt-2">
              · dagen tot concreet<br />resultaat ·
            </div>
          </div>
        </div>
      </section>

      <footer className="bg-[#1B1B3A] border-t border-white/10">
        <div className="max-w-[1500px] mx-auto px-6 lg:px-12 py-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <Brand variant="dark" />
          <div className="font-condensed uppercase text-xs tracking-[0.25em] text-white/50 font-semibold">
            © {new Date().getFullYear()} Techport · Vertrouwelijk
          </div>
        </div>
      </footer>
    </div>
  );
}
